import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { tillSessionService, TillSessionDTO } from '../services/tillSessionService';
import { useAuth } from './AuthContext';

// ─── Cache helpers ─────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  hasActiveTill: boolean;
  sessionId: number | null;
  cachedAt: number;
}

// Cache is keyed by userId so sessions never bleed across users or companies.
function cacheKey(userId: number | undefined): string {
  return `till_session_cache_${userId ?? 'anon'}`;
}

function readCache(userId: number | undefined): CacheEntry | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey(userId));
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

function writeCache(userId: number | undefined, hasActiveTill: boolean, sessionId: number | null): void {
  const entry: CacheEntry = { hasActiveTill, sessionId, cachedAt: Date.now() };
  localStorage.setItem(cacheKey(userId), JSON.stringify(entry));
}

function clearCache(userId: number | undefined): void {
  localStorage.removeItem(cacheKey(userId));
}

// Remove any legacy unscoped cache entry left by the old code.
function clearLegacyCache(): void {
  localStorage.removeItem('till_session_cache');
}

// ─── Context types ─────────────────────────────────────────────────────────────

interface TillContextType {
  activeTill: TillSessionDTO | null;
  loading: boolean;
  refreshTill: () => Promise<void>;
  clearTill: () => void;
}

const TillContext = createContext<TillContextType | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const TillProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user }    = useAuth();
  const userId                       = user?.id;
  const [activeTill, setActiveTill]  = useState<TillSessionDTO | null>(null);
  const [loading, setLoading]        = useState(true);

  const fetchTill = useCallback(async (uid: number | undefined) => {
    setLoading(true);
    try {
      const res    = await tillSessionService.getMyActive();
      const status = res.data?.data;
      if (status?.hasActiveTill && status.session) {
        setActiveTill(status.session);
        writeCache(uid, true, status.session.id);
      } else {
        setActiveTill(null);
        writeCache(uid, false, null);
      }
    } catch {
      // Network error — keep previous state, just clear cache so next load retries
      clearCache(uid);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever the authenticated user changes (covers both login/logout
  // and switching between company accounts without an explicit logout).
  useEffect(() => {
    clearLegacyCache();
    if (!isAuthenticated || !userId) {
      setActiveTill(null);
      clearCache(userId);
      setLoading(false);
      return;
    }
    // Authenticated: use cache to skip spinner when TTL hasn't expired,
    // but always fetch in background to stay fresh.
    const cache = readCache(userId);
    if (cache && !cache.hasActiveTill) {
      setActiveTill(null);
      setLoading(false);
    }
    fetchTill(userId);
  }, [isAuthenticated, userId, fetchTill]);

  const refreshTill = useCallback(async () => {
    clearCache(userId);
    await fetchTill(userId);
  }, [fetchTill, userId]);

  const clearTill = useCallback(() => {
    setActiveTill(null);
    clearCache(userId);
  }, [userId]);

  return (
    <TillContext.Provider value={{ activeTill, loading, refreshTill, clearTill }}>
      {children}
    </TillContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTill(): TillContextType {
  const ctx = useContext(TillContext);
  if (!ctx) throw new Error('useTill must be used inside TillProvider');
  return ctx;
}
